// GraphQL query strings for EPIC.
// Field selections are scoped to what the add-in's screens need today; expand
// per-query as new fields are required.

const CONTACT_FIELDS = /* GraphQL */ `
  id
  reference_number
  display_name
  title
  firstname
  middle_name
  lastname
  email_1
  email_2
  email_3
  # Phone numbers — digits-only split parts (backend E.164 redesign 2026-08-22):
  # {slot}_country_code / {slot}_number / {slot}_extension (no '+', digits only).
  # The dialable string is composed client-side — see ContactDetail.composePhone.
  mobile_phone_country_code
  mobile_phone_number
  mobile_phone_extension
  phone_home_1_country_code
  phone_home_1_number
  phone_home_1_extension
  phone_home_2_country_code
  phone_home_2_number
  phone_home_2_extension
  phone_business_1_country_code
  phone_business_1_number
  phone_business_1_extension
  phone_business_2_country_code
  phone_business_2_number
  phone_business_2_extension
  phone_business_fax_country_code
  phone_business_fax_number
  phone_business_fax_extension
  mobile_phone_can_text
  phone_home_1_can_text
  phone_home_2_can_text
  phone_business_1_can_text
  phone_business_2_can_text
  job_title
  department
  business_homepage
  office_location
  business {
    id
    reference_number
    name
  }
  rating
  has_headshot
  headshot_sharepoint_drive_id
  headshot_sharepoint_item_id
  record_title
  emoji
  model_connection_title
`;

const BUSINESS_FIELDS = /* GraphQL */ `
  id
  reference_number
  name
  short_business_name
  full_business_name
  link_business
  record_title
  emoji
  model_connection_title
`;

const BUSINESS_ACCOUNT_FIELDS = /* GraphQL */ `
  id
  reference_number
  account_name
  account_number
  currency_code
  business {
    id
    reference_number
    name
  }
  record_title
  emoji
  model_connection_title
`;

const CASE_FIELDS = /* GraphQL */ `
  id
  reference_number
  type
  name
  status
  priority
  scheduled_start
  scheduled_end
  actual_start
  actual_end
  ai_summary
  remarks
  record_title
  emoji
  model_connection_title
`;

// Task: status is String here but conflicts with Case.CaseStatus! and
// Asset.AssetStatus! when merged in the Linkable union. We don't use it
// (overdue is computed from due/completed), so drop it.
const TASK_FIELDS = /* GraphQL */ `
  id
  name
  importance
  due
  completed
  record_title
  emoji
  model_connection_title
`;

// Note.name is String (nullable) which conflicts with Business/Case/Task/Car
// where name is String!. We use record_title for display, so drop name.
const NOTE_FIELDS = /* GraphQL */ `
  id
  flagged
  record_title
  emoji
  model_connection_title
`;

// Address.name same nullability conflict — drop and use record_title / full_address.
const ADDRESS_FIELDS = /* GraphQL */ `
  id
  reference_number
  street
  city
  state
  postal_code
  full_address
  record_title
  emoji
  model_connection_title
`;

const CAR_FIELDS = /* GraphQL */ `
  id
  name
  year
  make
  model
  trim
  color
  record_title
  emoji
  model_connection_title
`;

const CAR_RENTAL_FIELDS = /* GraphQL */ `
  id
  reference_number
  reservation_number
  rental_agreement_number
  invoice_number
  business {
    id
    reference_number
    name
  }
  car_make_model
  car_class_given
  car_vin
  scheduled_pickup_datetime
  scheduled_dropoff_datetime
  record_title
  emoji
  model_connection_title
`;

// Asset.status (AssetStatus!) conflicts with Case.status (CaseStatus!) under
// Linkable union merge. We don't read it, so drop it.
const ASSET_FIELDS = /* GraphQL */ `
  id
  reference_number
  model_name
  model_number
  serial_number
  record_title
  emoji
  model_connection_title
`;

const TRACKING_NUMBER_FIELDS = /* GraphQL */ `
  id
  tracking_number
  courier {
    id
    name
  }
  record_title
  emoji
  model_connection_title
`;

// Slim selection — we never display Email content directly in the UI; only
// need the id to anchor connections_for_email. record_title/emoji/
// model_connection_title were dropped because the backend's resolver returns
// null for those (schema declares them non-nullable, query errored).
const EMAIL_FIELDS = /* GraphQL */ `
  id
  internet_message_id
  subject
`;

// Inline fragments for the Linkable union — used wherever a query returns
// `Linkable` (entity_one/entity_two on ModelConnection, search_entities, etc.).
const LINKABLE_FRAGMENTS = /* GraphQL */ `
  __typename
  ... on Contact {
    ${CONTACT_FIELDS}
  }
  ... on Business {
    ${BUSINESS_FIELDS}
  }
  ... on BusinessAccount {
    ${BUSINESS_ACCOUNT_FIELDS}
  }
  ... on Case {
    ${CASE_FIELDS}
  }
  ... on Task {
    ${TASK_FIELDS}
  }
  ... on Note {
    ${NOTE_FIELDS}
  }
  ... on Address {
    ${ADDRESS_FIELDS}
  }
  ... on Car {
    ${CAR_FIELDS}
  }
  ... on CarRental {
    ${CAR_RENTAL_FIELDS}
  }
  ... on Asset {
    ${ASSET_FIELDS}
  }
  ... on TrackingNumber {
    ${TRACKING_NUMBER_FIELDS}
  }
  ... on Email {
    ${EMAIL_FIELDS}
  }
  # Universal HasModelConnections fields for EVERY Linkable member — present
  # and future — via the backend DisplayableRecord / ReferenceNumbered
  # interfaces (epic-laravel#50). All 37 union members implement
  # DisplayableRecord (13 also implement ReferenceNumbered), so these two
  # inline fragments cover the whole union: a new backend entity type renders
  # here with ZERO add-in change. Types with richer UI still add their
  # type-specific fields via the ... on <Type> fragments above; the universal
  # fields simply merge. The mapper renders unrecognized types generically
  # from these fields (entityFromLinkable's default branch).
  ... on DisplayableRecord {
    id
    record_title
    emoji
    model_connection_title
  }
  ... on ReferenceNumbered {
    reference_number
  }
`;

const MODEL_CONNECTION_FIELDS = /* GraphQL */ `
  id
  entity_one {
    ${LINKABLE_FRAGMENTS}
  }
  entity_two {
    ${LINKABLE_FRAGMENTS}
  }
  entity_one_role {
    id
    name
    display_name
  }
  entity_two_role {
    id
    name
    display_name
  }
  start
  end
  remarks
`;
// is_user_editable dropped: the backend resolver returns null for it on
// freshly-created connections (e.g. EMAIL→CASE), and the schema declares it
// non-nullable, so selecting it aborts the whole response even though the
// row was written. The add-in never reads this field. Same class of bug as
// the EMAIL_FIELDS note above.

// ── Queries ────────────────────────────────────────────────────────────

export const RESOLVE_CONTACTS_QUERY = /* GraphQL */ `
  query ResolveContacts($emails: [String!]!) {
    resolve_contacts(emails: $emails) {
      email
      status
      contact {
        ${CONTACT_FIELDS}
      }
      business {
        ${BUSINESS_FIELDS}
      }
    }
  }
`;

export const CONTACT_QUERY = /* GraphQL */ `
  query Contact($id: ID!) {
    contact(id: $id) {
      ${CONTACT_FIELDS}
    }
  }
`;

export const EMAIL_BY_INTERNET_MESSAGE_ID_QUERY = /* GraphQL */ `
  query EmailByInternetMessageId($internet_message_id: String!) {
    email(internet_message_id: $internet_message_id) {
      ${EMAIL_FIELDS}
    }
  }
`;

export const CONNECTIONS_FOR_EMAIL_QUERY = /* GraphQL */ `
  query ConnectionsForEmail($email_message_id: ID!) {
    connections_for_email(email_message_id: $email_message_id) {
      ${MODEL_CONNECTION_FIELDS}
    }
  }
`;

export const SEARCH_ENTITIES_QUERY = /* GraphQL */ `
  query SearchEntities($kind: EntityKind!, $q: String!, $limit: Int) {
    search_entities(kind: $kind, q: $q, limit: $limit) {
      ${LINKABLE_FRAGMENTS}
    }
  }
`;

export const SEARCH_ALL_QUERY = /* GraphQL */ `
  query SearchAll($q: String!, $limit: Int) {
    search_all(q: $q, limit: $limit) {
      ${LINKABLE_FRAGMENTS}
    }
  }
`;

export const RECENT_ENTITIES_QUERY = /* GraphQL */ `
  query RecentEntities($limit: Int, $direction: SortOrder, $kind: EntityKind) {
    recent_entities(limit: $limit, direction: $direction, kind: $kind) {
      interactable {
        ${LINKABLE_FRAGMENTS}
      }
      last_interacted_at
      interaction_count
    }
  }
`;

// ── Mutations ─────────────────────────────────────────────────────────

export const BULK_CREATE_CONNECTIONS_MUTATION = /* GraphQL */ `
  mutation BulkCreateConnections($input: [CreateConnectionInput!]!) {
    bulk_create_connections(input: $input) {
      created
      skipped_existing
      connections {
        ${MODEL_CONNECTION_FIELDS}
      }
    }
  }
`;

export const CREATE_CASE_FROM_EMAIL_MUTATION = /* GraphQL */ `
  mutation CreateCaseFromEmail($input: CreateCaseFromEmailInput!) {
    create_case_from_email(input: $input) {
      id
      reference_number
      name
    }
  }
`;

// Pull an untracked email into EPIC on demand: the backend searches every
// mailbox over live Graph and force-imports the first copy it finds. Runs
// synchronously (several seconds); a "not in any mailbox" outcome comes back
// as success:false with email:null (a normal response, not a GraphQL error) —
// see EmailMessageResolver@reimportEmailFromOutlook.
export const REIMPORT_EMAIL_FROM_OUTLOOK_MUTATION = /* GraphQL */ `
  mutation ReimportEmailFromOutlook($internet_message_id: String!) {
    reimport_email_from_outlook(internet_message_id: $internet_message_id) {
      success
      message
      found_in_mailbox_email
      mailboxes_searched
      email {
        ${EMAIL_FIELDS}
      }
    }
  }
`;
